import { ReactElement, ReactNode, useState, useEffect, useMemo } from 'react';
import { RegionContext } from './context';
import { RegionOption, ResolvedRegion, RegionConfig } from './models';
import {
    REGION_CONFIGS,
    REGION_STORAGE_KEY,
    MULTI_REGION_ENABLED
} from './constants';
import {
    detectRegionFromGeoIp,
    detectRegionFromTimezone,
    getCachedGeoIpRegion
} from './geo-ip-service';
import { useNullableTokens } from '../tokens-provider';

type Props = {
    readonly children?: ReactNode;
};

function getTokenRegion(
    tokens: { accessTokenPayload: { region?: string } } | null
): ResolvedRegion | undefined {
    const region = tokens?.accessTokenPayload.region;
    if (region === 'eu' || region === 'us') {
        return region;
    }
    return undefined;
}

export function RegionProvider({ children }: Props): ReactElement {
    const { tokens } = useNullableTokens();
    const tokenRegion = getTokenRegion(tokens);

    // User's selection (persisted)
    const [selectedRegion, setSelectedRegion] = useState<RegionOption>(() => {
        const stored = localStorage.getItem(REGION_STORAGE_KEY);
        if (stored === 'auto' || stored === 'eu' || stored === 'us') {
            return stored;
        }
        return 'auto';
    });

    // Resolved region when no token region (for auto mode geo IP detection)
    // Initialize with cached geo IP if available, otherwise use timezone detection
    const [autoResolvedRegion, setAutoResolvedRegion] =
        useState<ResolvedRegion>(
            () => getCachedGeoIpRegion() ?? detectRegionFromTimezone()
        );
    const [isLoading, setIsLoading] = useState(false);

    // Detect geo IP when in auto mode and not authenticated
    useEffect(() => {
        if (tokenRegion || selectedRegion !== 'auto') {
            return;
        }
        setIsLoading(true);
        void detectRegionFromGeoIp()
            .then(setAutoResolvedRegion)
            .finally(() => setIsLoading(false));
    }, [selectedRegion, tokenRegion]);

    // Final resolved region: token region takes priority, then user selection
    const resolvedRegion: ResolvedRegion = useMemo(() => {
        // When multi-region is disabled, always use EU
        if (!MULTI_REGION_ENABLED) {
            return 'eu';
        }
        if (tokenRegion) {
            return tokenRegion;
        }
        if (selectedRegion === 'auto') {
            return autoResolvedRegion;
        }
        return selectedRegion;
    }, [tokenRegion, selectedRegion, autoResolvedRegion]);

    const config: RegionConfig = REGION_CONFIGS[resolvedRegion];

    const setRegion = (region: RegionOption): void => {
        localStorage.setItem(REGION_STORAGE_KEY, region);
        setSelectedRegion(region);
    };

    return (
        <RegionContext.Provider
            value={{
                selectedRegion,
                resolvedRegion,
                autoDetectedRegion: autoResolvedRegion,
                config,
                isLoading: !tokenRegion && isLoading,
                setRegion
            }}
        >
            {children}
        </RegionContext.Provider>
    );
}
